import { message } from 'antd';
import { JobAssignment, Filters } from '../hooks/useJobAssignments';
import { getJobAssignmentsOverview } from '../api/recruiter';

// Function to aggregate assignments by company (same logic as in hook)
const aggregateByCompany = (assignments: JobAssignment[], sortField?: string): JobAssignment[] => {
    const companyGroups: { [key: string]: JobAssignment[] } = {};

    // Group assignments by company name
    assignments.forEach(assignment => {
        const companyKey = assignment.recruiter_company_name;
        if (!companyGroups[companyKey]) {
            companyGroups[companyKey] = [];
        }
        companyGroups[companyKey].push(assignment);
    });

    // Merge data for each company group
    const aggregated = Object.values(companyGroups).map(groupAssignments => {
        // Sort by assignment date (oldest first) to prioritize older data
        const sortedAssignments = groupAssignments.sort((a, b) =>
            new Date(a.assignment_date).getTime() - new Date(b.assignment_date).getTime()
        );

        const baseAssignment = sortedAssignments[0]; // Use oldest as base
        const activeAssignments = groupAssignments.filter(a => a.is_active);
        const canEnrichAny = groupAssignments.some(a => a.can_answer_extension_fields);

        // Create aggregated assignment
        const aggregatedAssignment: JobAssignment = {
            ...baseAssignment,
            assignment_id: `aggregated-${baseAssignment.recruiter_company_name.replace(/\s+/g, '-').toLowerCase()}`,
            recruiter_name: groupAssignments.length > 1
                ? `${groupAssignments.length} users`
                : baseAssignment.recruiter_name,
            recruiter_email: groupAssignments.length > 1
                ? 'Multiple contacts'
                : baseAssignment.recruiter_email,
            is_active: activeAssignments.length > 0, // Active if any assignment is active
            can_answer_extension_fields: canEnrichAny, // Can enrich if any user can enrich
            assignment_count: groupAssignments.length,
            // Use oldest assignment date
            assignment_date: baseAssignment.assignment_date,
            // For other fields, prefer filled-in values or use oldest
            current_target_type: groupAssignments.find(a => a.current_target_type)?.current_target_type || baseAssignment.current_target_type,
            current_target_value: groupAssignments.find(a => a.current_target_value)?.current_target_value || baseAssignment.current_target_value,
            fee_type: groupAssignments.find(a => a.fee_type)?.fee_type || baseAssignment.fee_type,
        };

        return aggregatedAssignment;
    });

    // Apply sorting if specified
    if (sortField) {
        return aggregated.sort((a, b) => {
            const isDesc = sortField.startsWith('-');
            const field = isDesc ? sortField.substring(1) : sortField;

            let aValue: any = a[field as keyof JobAssignment];
            let bValue: any = b[field as keyof JobAssignment];

            // Handle special cases for sorting
            if (field === 'recruiter_name') {
                aValue = a.recruiter_company_name; // Sort by company name when aggregated
                bValue = b.recruiter_company_name;
            } else if (field === 'assignment_date') {
                aValue = new Date(a.assignment_date).getTime();
                bValue = new Date(b.assignment_date).getTime();
            } else if (field === 'job_title') {
                aValue = a.job_title.toLowerCase();
                bValue = b.job_title.toLowerCase();
            }

            if (aValue < bValue) return isDesc ? 1 : -1;
            if (aValue > bValue) return isDesc ? -1 : 1;
            return 0;
        });
    }

    return aggregated;
};

// Format time duration for display
const formatDuration = (seconds: number): string => {
    if (seconds < 60) return `${Math.round(seconds)}s`;
    if (seconds < 3600) return `${Math.round(seconds / 60)}m ${Math.round(seconds % 60)}s`;
    return `${Math.floor(seconds / 3600)}h ${Math.round((seconds % 3600) / 60)}m`;
};

// Global abort controller for cancellation
let exportAbortController: AbortController | null = null;

export const exportJobAssignmentsToCSV = async (
    filters: Filters,
    t: (key: string, fallback?: string) => string,
    onProgress?: (current: number, total: number, recordsCount: number, timeRemaining: string) => void
) => {
    try {
        // Create abort controller for cancellation
        exportAbortController = new AbortController();

        // Initial loading message with warning
        const startingMessage = `${t('EXPORT_STARTING', 'Preparing export...')} | ⚠️ ${t('EXPORT_WARNING', 'Please keep this tab open during export')}`;
        message.loading({
            content: startingMessage,
            key: 'export',
            duration: 0 // Don't auto-hide
        });

        // Fetch ALL data for export by removing pagination and getting all records
        let allAssignments: JobAssignment[] = [];
        let page = 1;
        let hasMoreData = true;
        let totalPages = 1;
        const startTime = Date.now();

        // Clean filters - remove undefined values for API call
        const cleanedFilters: any = {};
        Object.entries(filters).forEach(([key, value]) => {
            if (value !== undefined && value !== '' && key !== 'sort' && key !== 'aggregateByCompany') {
                cleanedFilters[key] = value;
            }
        });

        // Fetch all pages with progress tracking
        while (hasMoreData && !exportAbortController.signal.aborted) {

            const response = await getJobAssignmentsOverview({
                ...cleanedFilters,
                page,
            });

            const assignments = response.data.results;
            allAssignments.push(...assignments);

            // Update total pages on first request
            if (page === 1) {
                totalPages = response.data.pagination.total_pages;
            }

            // Calculate progress and time estimates
            const pageEndTime = Date.now();
            const avgTimePerPage = (pageEndTime - startTime) / (page * 1000); // average seconds per page
            const remainingPages = totalPages - page;
            const estimatedTimeRemaining = remainingPages * avgTimePerPage;

            // Update progress message
            const progressText = remainingPages > 0
                ? `📄 Fetching page ${page} of ${totalPages} | 📊 ${allAssignments.length} records | ⏱️ ~${formatDuration(estimatedTimeRemaining)} remaining | ⚠️ Keep this tab open!`
                : `📄 Page ${page} of ${totalPages} | 📊 ${allAssignments.length} records | 🔄 Processing data...`;

            message.loading({
                content: progressText,
                key: 'export',
                duration: 0
            });

            // Call progress callback if provided
            if (onProgress) {
                onProgress(page, totalPages, allAssignments.length, formatDuration(estimatedTimeRemaining));
            }

            // Check if there are more pages
            hasMoreData = response.data.pagination.next !== null;
            page++;

            // Add small delay to prevent overwhelming the server
            if (hasMoreData) {
                await new Promise(resolve => setTimeout(resolve, 100));
            }
        }

        // Check if export was cancelled
        if (exportAbortController.signal.aborted) {
            message.info({
                content: t('EXPORT_CANCELLED', 'Export cancelled'),
                key: 'export'
            });
            return;
        }

        // Processing message
        const processingText = `🔄 ${t('EXPORT_PROCESSING_DATA', 'Processing data...')} | 📊 Preparing ${allAssignments.length} records for download`;
        message.loading({
            content: processingText,
            key: 'export',
            duration: 0
        });

        // Apply aggregation if enabled
        if (filters.aggregateByCompany) {
            allAssignments = aggregateByCompany(allAssignments, filters.sort);
        }

        if (allAssignments.length === 0) {
            message.warning({ content: t('NO_DATA_TO_EXPORT', 'No data available to export'), key: 'export' });
            return;
        }

        // Create CSV headers
        const headers = filters.aggregateByCompany ? [
            'Agency Company Name',
            'User Count',
            'Contact Info',
            'Job Title',
            'Job Company',
            'Job Department',
            'Job Status',
            'Assignment Status',
            'Target Type',
            'Target Value',
            'Can Enrich',
            'First Assignment Date'
        ] : [
            'Agency User Name',
            'Agency User Email',
            'Agency Company Name',
            'Job Title',
            'Job Company',
            'Job Department',
            'Job Status',
            'Assignment Status',
            'Target Type',
            'Target Value',
            'Can Enrich',
            'Assignment Date'
        ];

        // Convert assignments to CSV rows
        const csvRows = allAssignments.map(assignment => {
            if (filters.aggregateByCompany) {
                return [
                    assignment.recruiter_company_name,
                    assignment.assignment_count.toString(),
                    assignment.recruiter_email,
                    assignment.job_title,
                    assignment.job_company_name,
                    assignment.job_department || '',
                    assignment.job_status,
                    assignment.is_active ? 'Active' : 'Inactive',
                    assignment.current_target_type || '',
                    assignment.current_target_value || '',
                    assignment.can_answer_extension_fields ? 'Yes' : 'No',
                    new Date(assignment.assignment_date).toLocaleDateString()
                ];
            } else {
                return [
                    assignment.recruiter_name,
                    assignment.recruiter_email,
                    assignment.recruiter_company_name,
                    assignment.job_title,
                    assignment.job_company_name,
                    assignment.job_department || '',
                    assignment.job_status,
                    assignment.is_active ? 'Active' : 'Inactive',
                    assignment.current_target_type || '',
                    assignment.current_target_value || '',
                    assignment.can_answer_extension_fields ? 'Yes' : 'No',
                    new Date(assignment.assignment_date).toLocaleDateString()
                ];
            }
        });

        // Combine headers and data
        const csvContent = [headers, ...csvRows]
            .map(row => row.map(field => `"${String(field).replace(/"/g, '""')}"`).join(','))
            .join('\n');

        // Create and download the file
        const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
        const link = document.createElement('a');

        if (link.download !== undefined) {
            const url = URL.createObjectURL(blob);
            link.setAttribute('href', url);

            // Generate filename with current date
            const today = new Date().toISOString().split('T')[0];
            link.setAttribute('download', `job-assignments-${today}.csv`);

            link.style.visibility = 'hidden';
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);

            const totalTime = (Date.now() - startTime) / 1000;
            const successText = `✅ ${t('EXPORT_SUCCESS', `Exported ${allAssignments.length} job assignments successfully`)} | ⏱️ Completed in ${formatDuration(totalTime)}`;
            message.success({
                content: successText,
                key: 'export',
                duration: 5
            });
        } else {
            throw new Error('Browser does not support file downloads');
        }
    } catch (error) {
        console.error('Export failed:', error);

        if (exportAbortController?.signal.aborted) {
            // Don't show error if it was deliberately cancelled
            return;
        }

        const errorText = `${t('EXPORT_ERROR', 'Failed to export job assignments')} | 💡 ${t('EXPORT_ERROR_TIP', 'Try refreshing the page and exporting again')}`;
        message.error({
            content: errorText,
            key: 'export',
            duration: 8
        });
    } finally {
        exportAbortController = null;
    }
};

// Function to cancel ongoing export
export const cancelExport = () => {
    if (exportAbortController) {
        exportAbortController.abort();
        exportAbortController = null;
    }
}; 